import { useEffect, useState } from "react";
import type { EstimatorResult, MoneyRange } from "@reno-estimator/core";
import { formatMoney, formatMoneyRange, formatDate } from "../../lib/format.js";
import { PROJECT_TYPE_LABELS } from "@reno-estimator/core";
import type { EstimatorFlow } from "../../state/useEstimatorFlow.js";
import { LeadCaptureForm } from "./LeadCaptureForm.js";
import { EmailEstimateForm } from "../estimate/EmailEstimateForm.js";

const CONFIDENCE_LABEL: Record<EstimatorResult["estimateConfidence"], string> = {
  low: "Low",
  medium: "Medium",
  high: "High"
};

const INPUT_COMPLETENESS_LABEL: Record<EstimatorResult["inputCompleteness"], string> = {
  preliminary: "Preliminary",
  moderate: "Moderate",
  higher_confidence: "Higher confidence"
};

/**
 * Why each input-completeness rating was assigned, in plain language tied
 * to what the rating actually measures - the specificity/completeness of
 * the user's own answers, not the pricing data. Kept short: the detailed
 * "what would improve this" list (inputCompletenessNotes) does the rest.
 */
const INPUT_COMPLETENESS_REASON: Record<EstimatorResult["inputCompleteness"], string> = {
  preliminary:
    "Several of your answers were broad ranges or \"unknown\" rather than specific figures, so this is an early, rough number.",
  moderate:
    "Most of your answers were specific, but a few details are still approximate or unconfirmed, which keeps this a mid-level estimate.",
  higher_confidence:
    "Your answers were specific and largely confirmed, so this estimate is as tight as a planning-level number can be before a site visit."
};

/**
 * Anchor/base line items (e.g. "base_renovation", "finishing_base",
 * "base_construction (deck)") represent the unavoidable base cost of the
 * project's size and type, not a discretionary choice. They dominate the
 * cost-drivers list by raw dollar amount if not excluded, which defeats
 * the point of showing what SELECTIONS are moving the estimate.
 */
export function isAnchorLineItem(label: string): boolean {
  return label === "base_renovation" || label === "finishing_base" || label.startsWith("base_construction");
}

/**
 * Cost-driver labels come straight from the pricing dataset's internal
 * category/subcategory keys (e.g. "cabinetry (semi_custom)",
 * "permit_design_allowance") - fine for an audit log, not for a "clear,
 * premium" results screen. This turns snake_case keys into plain words
 * without needing an exhaustive per-category display-name table.
 */
export function humanizeLabel(label: string): string {
  const match = /^([a-z0-9_]+)(?: \(([a-z0-9_]+)\))?$/.exec(label);
  if (!match) return label;
  const [, category, subcategory] = match;
  const words = (s: string) => s.replace(/_/g, " ");
  // The first capture group is not optional in the pattern above (only the
  // "(subcategory)" group is) so it's always defined when `match` succeeds -
  // TypeScript can't infer that from a regex literal, hence the fallback.
  const categoryText = words(category ?? label);
  const capitalized = categoryText.charAt(0).toUpperCase() + categoryText.slice(1);
  return subcategory ? `${capitalized} (${words(subcategory)})` : capitalized;
}

export function moneyRangeIsZero(range: MoneyRange): boolean {
  return range.low.amount === 0 && range.typical.amount === 0 && range.high.amount === 0;
}

function BreakdownRow({ label, range }: { label: string; range: MoneyRange }) {
  return (
    <div className="breakdown-row">
      <span className="breakdown-row__label">{label}</span>
      <span className="breakdown-row__value">{formatMoneyRange(range)}</span>
    </div>
  );
}

export function Step7Estimate({ flow }: { flow: EstimatorFlow }) {
  const [howCalculatedOpen, setHowCalculatedOpen] = useState(false);

  useEffect(() => {
    if (flow.result.status === "idle") {
      void flow.calculate();
    }
  }, [flow.result.status, flow]);

  if (!flow.projectType) return null;

  if (flow.result.status === "loading" || flow.result.status === "idle") {
    return (
      <section className="step" aria-live="polite">
        <div className="step__content estimate-loading">
          <p role="status">Calculating your estimate…</p>
        </div>
      </section>
    );
  }

  if (flow.result.status === "error") {
    return (
      <section className="step" aria-live="assertive">
        <div className="step__content">
          <p className="estimate-error">{flow.result.message}</p>
          <button type="button" className="btn btn--primary" onClick={() => void flow.calculate()}>
            Try again
          </button>
        </div>
        <div className="step__nav">
          <button type="button" className="btn btn--ghost" onClick={flow.goBack}>
            <span aria-hidden="true">←</span> Back
          </button>
        </div>
      </section>
    );
  }

  const result = flow.result.data;
  const input = flow.result.input;
  const noMethodology = result.estimateConfidence === "low" && result.isPlaceholderPricing;
  const projectLabel = PROJECT_TYPE_LABELS[flow.projectType];

  const otherNotices = result.warnings.filter((w) => w.code !== "sample_pricing_data" && w.code !== "estimate_outside_expected_range" && w.code !== "placeholder_pricing_data");

  const drivers = [...result.explanation.steps]
    .filter(
      (s) =>
        s.amount &&
        s.amount.amount > 0 &&
        s.stepId !== "regional_multiplier" &&
        s.stepId !== "contingency" &&
        !isAnchorLineItem(s.label)
    )
    .sort((a, b) => (b.amount?.amount ?? 0) - (a.amount?.amount ?? 0))
    .slice(0, 5);

  const breakdownRows: { label: string; range: MoneyRange }[] = [
    { label: "Labour", range: result.breakdown.labour },
    { label: "Materials", range: result.breakdown.materials },
    { label: "Fixtures & appliances", range: result.breakdown.fixturesAndAppliances },
    { label: "Permits, design & misc.", range: result.breakdown.permitsDesignAndMiscAllowance }
  ].filter((row) => !moneyRangeIsZero(row.range));

  return (
    <section className="step estimate" aria-labelledby="step-heading">
      <div className="step__content">
        <h1 id="step-heading" className="step__title">
          Your estimate
        </h1>
        <p className="estimate__trust-badge">Planning-level estimate — not a quotation</p>

        {noMethodology ? (
          <div className="estimate__unsupported" role="status">
            <h2>We don't have detailed pricing for {projectLabel.toLowerCase()} yet</h2>
            <p>
              Our calculator currently produces detailed estimates for kitchen, bathroom, basement, and deck/patio
              projects. We've recorded your answers, but we can't responsibly show a dollar figure for this project
              type without real pricing data behind it — we'd rather tell you that than guess.
            </p>
            <p>Try a kitchen, bathroom, basement, or deck/patio project to see a full example, or reach out directly for a project like this one.</p>
          </div>
        ) : (
          <>
            <div className="estimate__headline">
              <p className="estimate__headline-label">Estimated construction budget</p>
              <p className="estimate__headline-range">{formatMoneyRange(result.estimate)}</p>
              <p className="estimate__headline-typical">
                Typical comparable project: <strong>Approximately {formatMoney(result.estimate.typical)}</strong>
              </p>
              <dl className="meta-list meta-list--inline">
                <div>
                  <dt>Low estimate</dt>
                  <dd>{formatMoney(result.estimate.low)}</dd>
                </div>
                <div>
                  <dt>Typical estimate</dt>
                  <dd>{formatMoney(result.estimate.typical)}</dd>
                </div>
                <div>
                  <dt>High estimate</dt>
                  <dd>{formatMoney(result.estimate.high)}</dd>
                </div>
              </dl>
            </div>

            {result.requiresManualReview ? (
              <div className="estimate__notice" role="status">
                This estimate falls outside the typical planning range for this project type. Treat it as a rough starting
                point and get a professional assessment before budgeting further.
              </div>
            ) : null}

            <div className="estimate__grid">
              <div className="estimate-card">
                <h2>Cost breakdown</h2>
                <div className="breakdown-list">
                  {breakdownRows.map((row) => (
                    <BreakdownRow key={row.label} label={row.label} range={row.range} />
                  ))}
                </div>
              </div>

              <div className="estimate-card">
                <h2>Major cost drivers</h2>
                <p className="estimate-card__subtitle">What's pushing this estimate up, beyond the base cost of a project this size.</p>
                {drivers.length > 0 ? (
                  <ul className="driver-list">
                    {drivers.map((d) => (
                      <li key={d.stepId}>
                        <span>{humanizeLabel(d.label)}</span>
                        <span>{d.amount ? formatMoney(d.amount) : ""}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No individual selections are materially changing this estimate beyond the base project cost.</p>
                )}
              </div>

              <div className="estimate-card">
                <h2>Contingency & planning budget</h2>
                <div className="breakdown-list">
                  <div className="breakdown-row">
                    <span className="breakdown-row__label">Recommended contingency ({result.recommendedContingencyPercent}%)</span>
                    <span className="breakdown-row__value">{formatMoneyRange(result.recommendedContingencyAmount)}</span>
                  </div>
                  <div className="breakdown-row breakdown-row--emphasis">
                    <span className="breakdown-row__label">Recommended planning budget</span>
                    <span className="breakdown-row__value">{formatMoneyRange(result.planningBudget)}</span>
                  </div>
                </div>
              </div>

              <div className="estimate-card">
                <h2>Confidence & data</h2>
                <dl className="meta-list">
                  <div>
                    <dt>Estimate confidence</dt>
                    <dd>{INPUT_COMPLETENESS_LABEL[result.inputCompleteness]}</dd>
                  </div>
                  <div>
                    <dt>Pricing date</dt>
                    <dd>{formatDate(result.pricingDate)}</dd>
                  </div>
                  <div>
                    <dt>Pricing version</dt>
                    <dd>{result.pricingVersion}</dd>
                  </div>
                </dl>
                <p className="estimate__confidence-note">{INPUT_COMPLETENESS_REASON[result.inputCompleteness]}</p>
                <p className="estimate__confidence-note">
                  This reflects how complete and specific your answers were - not a formal appraisal. It is never a
                  substitute for a contractor's quotation.
                </p>
                {result.inputCompletenessNotes.length > 0 ? (
                  <>
                    <h3>What would improve this estimate</h3>
                    <ul className="text-list">
                      {result.inputCompletenessNotes.map((note, i) => (
                        <li key={i}>{note}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
                {result.isPlaceholderPricing ? (
                  <p className="estimate__sample-disclosure">
                    This estimate uses sample planning-level pricing, not verified real-world Canadian pricing. Treat every
                    figure here as illustrative until real pricing data is loaded. (Underlying pricing-data confidence:{" "}
                    {CONFIDENCE_LABEL[result.estimateConfidence]}.)
                  </p>
                ) : null}
              </div>

              {result.assumptions.length > 0 ? (
                <div className="estimate-card">
                  <h2>Assumptions</h2>
                  <ul className="text-list">
                    {result.assumptions.map((a) => (
                      <li key={a.code}>{a.message}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {result.exclusions.length > 0 ? (
                <div className="estimate-card">
                  <h2>What's excluded</h2>
                  <ul className="text-list">
                    {result.exclusions.map((e) => (
                      <li key={e.code}>{e.message}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {otherNotices.length > 0 ? (
                <div className="estimate-card">
                  <h2>Other notes</h2>
                  <ul className="text-list">
                    {otherNotices.map((w) => (
                      <li key={w.code}>{w.message}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>

            <div className="how-calculated">
              <button
                type="button"
                className="btn btn--link"
                aria-expanded={howCalculatedOpen}
                aria-controls="how-calculated-panel"
                onClick={() => setHowCalculatedOpen((v) => !v)}
              >
                {howCalculatedOpen ? "Hide" : "Show"} how we calculated this
              </button>
              {howCalculatedOpen ? (
                <div id="how-calculated-panel" className="how-calculated__panel">
                  <p>{result.explanation.summary}</p>
                  <h3>Your selections</h3>
                  <dl className="meta-list">
                    {result.explanation.inputSummary.map((fact) => (
                      <div key={fact.label}>
                        <dt>{fact.label}</dt>
                        <dd>{fact.value}</dd>
                      </div>
                    ))}
                  </dl>
                  <h3>Calculation steps</h3>
                  <ul className="text-list">
                    {result.explanation.steps.map((s) => (
                      <li key={s.stepId}>{s.detail}</li>
                    ))}
                  </ul>
                  <p className="how-calculated__pricing-version">
                    Pricing version {result.pricingVersion}, dated {formatDate(result.pricingDate)}.
                  </p>
                </div>
              ) : null}
            </div>

            <EmailEstimateForm projectType={flow.projectType} answers={flow.answers} result={result} />
            <LeadCaptureForm input={input} result={result} />
          </>
        )}
      </div>
      <div className="step__nav">
        <button type="button" className="btn btn--ghost" onClick={flow.goBack}>
          <span aria-hidden="true">←</span> Change answers
        </button>
        <button type="button" className="btn btn--primary" onClick={() => void flow.calculate()}>
          Recalculate
        </button>
      </div>
    </section>
  );
}
